import { generateRandomId } from '@uw/utils';
import type { BuildSsoUrl } from './use-sso.types';
import { OAUTH, SAML } from './use-sso.constants';

declare const applicationConfig: { base: { LALoginLink: string } };

const CALLER_ORIGIN_QUERY_PARAM = 'callerOrigin';
const REQUEST_ID_QUERY_PARAM = 'requestId';
const STATE_QUERY_PARAM = 'state';
const ERROR_MESSAGE_QUERY_PARAM = 'errorMessage';
const AUTH_CODE_QUERY_PARAM = 'authCode';

const OAUTH_REDIRECT_PARAM_PREFIX = 'oauth2';
const SAML_REDIRECT_PARAM_PREFIX = 'saml2';

const SSO_SUCCESS_PATH = 'sso/oauth-logged-in';
const SSO_FAILURE_PATH = 'sso/oauth-failed';
const SSO_PUBLIC_LOGIN_PATH = 'sso/v0/public/login';

type SupportedSsoProtocol = typeof OAUTH | typeof SAML;

const SSO_PROTOCOL_QUERY_PREFIX: Record<SupportedSsoProtocol, string> = {
  OAUTH: OAUTH_REDIRECT_PARAM_PREFIX,
  SAML: SAML_REDIRECT_PARAM_PREFIX,
};

export const isLocalhostOrigin = () =>
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

export const createPopupRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return generateRandomId();
};

const appendCallerOriginIfNeeded = (url: URL, localhostOrigin: boolean) => {
  if (!localhostOrigin) {
    url.searchParams.set(CALLER_ORIGIN_QUERY_PARAM, window.location.origin);
  }
};

const buildLaFailureRedirectUrl = (popupRequestId: string, localhostOrigin: boolean) => {
  const failureUrl = new URL(`${__API_BASE__}${SSO_FAILURE_PATH}`);
  appendCallerOriginIfNeeded(failureUrl, localhostOrigin);
  failureUrl.searchParams.set(STATE_QUERY_PARAM, popupRequestId);
  failureUrl.searchParams.set(ERROR_MESSAGE_QUERY_PARAM, '');

  return failureUrl;
};

const buildPopupSuccessRedirectUrl = (popupRequestId: string, localhostOrigin: boolean) => {
  const redirectUrl = new URL(`${__BASE_API_SSO__}${SSO_SUCCESS_PATH}`);
  appendCallerOriginIfNeeded(redirectUrl, localhostOrigin);
  redirectUrl.searchParams.set(REQUEST_ID_QUERY_PARAM, popupRequestId);
  redirectUrl.searchParams.set(AUTH_CODE_QUERY_PARAM, '');

  return redirectUrl;
};

const buildPopupFailureRedirectUrl = (popupRequestId: string, localhostOrigin: boolean) => {
  const failureUrl = new URL(`${__BASE_API_SSO__}${SSO_FAILURE_PATH}`);
  appendCallerOriginIfNeeded(failureUrl, localhostOrigin);
  failureUrl.searchParams.set(REQUEST_ID_QUERY_PARAM, popupRequestId);
  failureUrl.searchParams.set(ERROR_MESSAGE_QUERY_PARAM, '');

  return failureUrl;
};

const buildLaLoginUrl = (params: BuildSsoUrl, localhostOrigin: boolean) => {
  const url = new URL(applicationConfig.base.LALoginLink);
  const failureUrl = buildLaFailureRedirectUrl(params.popupRequestId, localhostOrigin);

  url.searchParams.set('userwayAccountCode', params.code);
  appendCallerOriginIfNeeded(url, localhostOrigin);
  url.searchParams.set('userwayState', params.popupRequestId);
  url.searchParams.set('userwayFailureRedirectUri', failureUrl.toString());

  return url.toString();
};

const buildStandardSsoUrl = (params: BuildSsoUrl, localhostOrigin: boolean) => {
  const protocol = params.ssoProtocol.toLowerCase();
  const queryParamPrefix = SSO_PROTOCOL_QUERY_PREFIX[params.ssoProtocol as SupportedSsoProtocol];
  const redirectUrl = buildPopupSuccessRedirectUrl(params.popupRequestId, localhostOrigin);
  const failureUrl = buildPopupFailureRedirectUrl(params.popupRequestId, localhostOrigin);

  const url = new URL(
    `${__BASE_API_SSO__}${SSO_PUBLIC_LOGIN_PATH}/${protocol}-init/${params.ssoPath}`,
  );
  url.searchParams.set(`${queryParamPrefix}_request_origin`, 'WIDGET_ORIGIN');
  url.searchParams.set(`${queryParamPrefix}_redirect_uri`, redirectUrl.toString());
  url.searchParams.set(`${queryParamPrefix}_failure_redirect_uri`, failureUrl.toString());
  url.searchParams.set(`${queryParamPrefix}_target_acc_code`, params.code);
  url.searchParams.set(`${queryParamPrefix}_target_site_id`, params.thisSiteId);

  return url.toString();
};

export const buildSsoUrl = (params: BuildSsoUrl): string => {
  const localhostOrigin = isLocalhostOrigin();

  return params.isLaLoginAllowed
    ? buildLaLoginUrl(params, localhostOrigin)
    : buildStandardSsoUrl(params, localhostOrigin);
};
